import { useState, useRef } from "react";
import { Imageprops } from ".";
type Props = {
  images: Record<string, Imageprops>;
  _selectedImageKey: string | null;
  onClose: () => void;
};
export function useImageGalleryLogic({
  images,
  _selectedImageKey,
  onClose,
}: Props) {
  const [selectedImageKey, setSelectedImageKey] = useState(_selectedImageKey);
  const keys = Object.keys(images);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const hasDragged = useRef(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    hasDragged.current = false;
    startX.current = e.pageX - (scrollRef.current?.offsetLeft || 0);
    scrollLeft.current = scrollRef.current?.scrollLeft || 0;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    e.preventDefault();
    hasDragged.current = true;
    const x = e.pageX - (scrollRef.current.offsetLeft || 0);
    const walk = x - startX.current;
    scrollRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const handleMouseUpOrLeave = () => {
    isDragging.current = false;
  };

  const toggleZoom = (e: React.MouseEvent) => {
    if (scale > 1) {
      // If you are already zoomed in, reset to normal state
      setScale(1);
      setPosition({ x: 0, y: 0 });
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;

      const newScale = 2;
      setScale(newScale);
      setPosition({
        x: (rect.width / 2 - offsetX) / newScale,
        y: (rect.height / 2 - offsetY) / newScale,
      });
    }
  };

  const handleImageClick = (key: string) => {
    if (hasDragged.current) return;
    setSelectedImageKey(key);
  };

  const handleNext = () => {
    if (selectedImageKey === null) return;
    const currentIndex = keys.indexOf(selectedImageKey);
    const nextIndex = (currentIndex + 1) % keys.length;
    setSelectedImageKey(keys[nextIndex]);
  };

  const handlePrev = () => {
    if (selectedImageKey === null) return;
    const currentIndex = keys.indexOf(selectedImageKey);
    const prevIndex = (currentIndex - 1 + keys.length) % keys.length;
    setSelectedImageKey(keys[prevIndex]);
  };

  const handleClose = () => {
    setSelectedImageKey(null);
    onClose();
  };

  return {
    handleClose,
    handlePrev,
    toggleZoom,
    scale,
    position,
    selectedImageKey,
    handleNext,
    scrollRef,
    keys,
    isDragging,
    handleImageClick,
    handleMouseUpOrLeave,
    handleMouseDown,
    handleMouseMove,
  };
}
